import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import styles from './FileUpload.module.scss'
import cn from 'classnames'
import { useDropzone } from 'react-dropzone'
import AddImgIcon from './add-img.svg'
import AddVideoIcon from './add-video.svg'
import RectangleIcon from './rectangle.svg'
import CloseIcon from '../../../public/static/icons/close.svg'
import { declOfNum } from '../../../lib/utils'
import { IUploadedFile } from '../../types'

interface IFileUpload {
  count: number
  files: IUploadedFile[]
  setFiles: Dispatch<SetStateAction<IUploadedFile[]>>
  fileType: 'image' | 'video'
  className?: string
}

const FileUpload: FC<IFileUpload> = ({
  count,
  files,
  setFiles,
  fileType,
  className,
}) => {
  const [error, setError] = useState('')
  const [isIOS, setIsIOS] = useState(false)
  const isImage = fileType === 'image'
  const titleText = `Добавьте до ${count} ${
    isImage
      ? declOfNum(count, ['фотографию', 'фотографии', 'фотографий'])
      : 'видео'
  }`
  const description = isImage
    ? 'HEIC, JPG, JPEG или PNG до 5 мб'
    : 'MP4 до 100 мб'
  const timeoutRef = useRef(null)

  const onDrop = (acceptedFiles) => {
    const updateFiles = [
      ...files,
      ...acceptedFiles
        .filter((file) => file.size < (isImage ? 5e6 : 1e8))
        .map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            id: `${Math.floor(new Date().getTime() / 1000)}-${
              file.lastModified
            }`,
          }),
        ),
    ]

    if (updateFiles.length < files.length + acceptedFiles.length) {
      clearTimeout(timeoutRef.current)
      setError('Превышен размер файла')

      timeoutRef.current = setTimeout(() => setError(''), 5000)
    }

    if (updateFiles.length > count) {
      clearTimeout(timeoutRef.current)
      setError('Достигнуто максимальное количество файлов')

      timeoutRef.current = setTimeout(() => setError(''), 5000)
    }

    setFiles(updateFiles.slice(0, count))
  }

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    accept: isImage
      ? {
          'image/jpeg': ['.jpeg'],
          'image/jpg': ['.jpg'],
          'image/png': ['.png'],
          'image/avif': ['.avif'],
          'image/heif': ['.heif'],
          'image/heic': ['.heic'],
          'image/x-png': ['.png'],
          'image/webp': ['.webp'],
          'image/gif': ['.gif'],
        }
      : {
          'video/mp4': ['.mp4'],
          'video/quicktime': ['.mov', '.m4v'],
          'video/x-prores': ['.prores'],
          'video/hevc': ['.hevc'],
          'application/x-pvt': ['.pvt'],
          'video/x-msvideo': ['.avi'],
          'video/mpeg': ['.m1v', '.m2v', '.mpa', '.mpe', '.mpeg', '.mpg'],
          'video/x-mpeg': ['.mpeg', '.mpg', '.mpe', '.mpv', '.mp2v'],
          'video/webm': ['.webm'],
          'video/3gpp': ['.3gp'],
          'video/3gpp2': ['.3g2'],
          'video/avi': ['.avi'],
        },
    onDrop,
    multiple: true,
  })

  useEffect(() => {
    if (fileRejections.length) {
      clearTimeout(timeoutRef.current)
      setError('Неверный формат файла')

      timeoutRef.current = setTimeout(() => setError(''), 5000)
    }
  }, [fileRejections])

  useEffect(() => {
    if (typeof window === 'undefined') return

    setIsIOS(
      window.navigator &&
        window.navigator.platform &&
        (/iP(ad|hone|od)/.test(window.navigator.platform) ||
          (window.navigator.platform === 'MacIntel' &&
            window.navigator.maxTouchPoints > 1)),
    )
  }, [])

  return (
    <div className={cn(styles.container, className)}>
      <p className={styles.text}>{titleText}</p>
      <p className={styles.desc}>{description}</p>
      <div {...getRootProps({ className: styles.content })}>
        <input {...getInputProps()} />
        <ul
          className={styles.list}
          onClick={(e) => {
            if (files.length === count) {
              e.stopPropagation()
            }
          }}
        >
          {!!files.length &&
            files.map((file, index) => (
              <li key={index} className={styles.imgContainer}>
                <button
                  className={styles.close}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setFiles(files.filter((item) => item.id !== file.id))
                  }}
                >
                  <CloseIcon />
                </button>
                {isImage ? (
                  <img src={file.preview} alt="preview" />
                ) : (
                  <video
                    src={file.preview}
                    poster={isIOS ? file.preview : undefined}
                  />
                )}
              </li>
            ))}
          {files.length < count && (
            <li className={styles.empty}>
              <RectangleIcon className={styles.rectangle} />
              {isImage ? <AddImgIcon /> : <AddVideoIcon />}
            </li>
          )}
        </ul>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    </div>
  )
}

export default FileUpload
